import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Truck, X } from 'lucide-react';

interface EquipmentItem {
  tipo: string;
  identificacao: string;
  condicao?: string;
  operador?: string;
  tempoOcioso?: string;
  observations?: string;
  status?: 'proprio' | 'locado';
  quantity?: number;
}

interface EquipmentCardsLayoutProps {
  equipment: EquipmentItem[];
  title?: string;
  showTotals?: boolean;
  onDelete?: (index: number) => void;
  onEdit?: (index: number) => void;
}

export const EquipmentCardsLayout: React.FC<EquipmentCardsLayoutProps> = ({
  equipment,
  title = "Equipamentos",
  showTotals = true,
  onDelete,
  onEdit
}) => {
  // Agrupar por status (próprio vs locado)
  const equipmentWithStatus = equipment.map(item => ({
    ...item,
    status: item.status || (item.identificacao?.includes('LOC') ? 'locado' : 'proprio')
  }));

  const proprios = equipmentWithStatus.filter(item => item.status === 'proprio');
  const locados = equipmentWithStatus.filter(item => item.status === 'locado');

  const totalProprios = proprios.length;
  const totalLocados = locados.length;
  const totalGeral = equipment.length;

  // Separate own and rented equipment groups
  const propriosEquipment = equipmentWithStatus.filter(item => item.status === 'proprio');
  const locadosEquipment = equipmentWithStatus.filter(item => item.status === 'locado');

  return (
    <div className="space-y-4">      
      {/* Own equipment row */}
      {propriosEquipment.length > 0 && (
        <div>
          <div className="text-sm font-medium text-blue-700 mb-2">Próprios</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {propriosEquipment.map((item, index) => {
              const originalIndex = equipment.findIndex(eq => 
                eq.tipo === item.tipo &&
                eq.identificacao === item.identificacao &&
                eq.operador === item.operador &&
                eq.quantity === item.quantity
              );
              
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-md transition-shadow max-w-36 border-blue-200 bg-blue-50/30 relative cursor-pointer"
                  onClick={() => {
                    if (onEdit && originalIndex >= 0) {
                      onEdit(originalIndex);
                    }
                  }}
                >
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute -top-0.5 -right-0.5 h-3 w-3 p-0 rounded-full bg-destructive/90 hover:bg-destructive shadow-sm border border-background/50 text-destructive-foreground hover:scale-105 transition-all duration-200 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (originalIndex >= 0) {
                          onDelete(originalIndex);
                        }
                      }}
                    >
                      <X className="h-2 w-2" />
                    </Button>
                  )}
                  <CardContent className="p-2">
                  <div className="space-y-1.5">
                    <div className="font-medium text-base text-center">{item.tipo}</div>
                    <div className="text-2xl font-bold text-center text-primary">
                      {item.quantity || 1}
                    </div>
                    {item.operador && (
                      <div className="text-xs text-muted-foreground text-center">
                        Op: {item.operador}
                      </div>
                    )}
                    {item.tempoOcioso && (
                      <div className="text-[10px] text-orange-600 text-center">
                        Ocioso: {item.tempoOcioso}
                      </div>
                    )}
                    {item.observations && (
                      <div className="text-[10px] text-muted-foreground mt-1.5 p-1.5 bg-muted/30 rounded">
                        {item.observations}
                      </div>
                    )}
                  </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Own equipment summary card */}
            {showTotals && totalProprios > 0 && (
              <Card className="border-2 border-blue-200 bg-blue-50 hover:shadow-md transition-shadow max-w-36">
                <CardContent className="p-2">
                  <div className="space-y-1.5">
                    <div className="font-medium text-base text-center text-blue-700">Total Próprios</div>
                    <div className="text-2xl font-bold text-center text-blue-600">
                      {totalProprios}
                    </div>
                    <div className="text-xs text-blue-600 text-center">
                      equipamentos
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
      
      {/* Rented equipment row */}
      {locadosEquipment.length > 0 && (
        <div>
          <div className="text-sm font-medium text-orange-700 mb-2">Locados</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {locadosEquipment.map((item, index) => {
              const originalIndex = equipment.findIndex(eq => 
                eq.tipo === item.tipo &&
                eq.identificacao === item.identificacao &&
                eq.operador === item.operador &&
                eq.quantity === item.quantity
              );
              
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-md transition-shadow max-w-36 border-orange-200 bg-orange-50/30 relative cursor-pointer"
                  onClick={() => {
                    if (onEdit && originalIndex >= 0) {
                      onEdit(originalIndex);
                    }
                  }}
                >
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute -top-0.5 -right-0.5 h-3 w-3 p-0 rounded-full bg-destructive/90 hover:bg-destructive shadow-sm border border-background/50 text-destructive-foreground hover:scale-105 transition-all duration-200 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (originalIndex >= 0) {
                          onDelete(originalIndex);
                        }
                      }}
                    >
                      <X className="h-2 w-2" />
                    </Button>
                  )}
                  <CardContent className="p-2">
                  <div className="space-y-1.5">
                    <div className="font-medium text-base text-center">{item.tipo}</div>
                    <div className="text-2xl font-bold text-center text-primary">
                      {item.quantity || 1}
                    </div>
                    {item.operador && (
                      <div className="text-xs text-muted-foreground text-center">
                        Op: {item.operador}
                      </div>
                    )}
                    {item.tempoOcioso && (
                      <div className="text-[10px] text-orange-600 text-center">
                        Ocioso: {item.tempoOcioso}
                      </div>
                    )}
                    {item.observations && (
                      <div className="text-[10px] text-muted-foreground mt-1.5 p-1.5 bg-muted/30 rounded">
                        {item.observations}
                      </div>
                    )}
                  </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Rented equipment summary card */}
            {showTotals && totalLocados > 0 && (
              <Card className="border-2 border-orange-200 bg-orange-50 hover:shadow-md transition-shadow max-w-36">
                <CardContent className="p-2">
                  <div className="space-y-1.5">
                    <div className="font-medium text-base text-center text-orange-700">Total Locados</div>
                    <div className="text-2xl font-bold text-center text-orange-600">
                      {totalLocados}
                    </div>
                    <div className="text-xs text-orange-600 text-center">
                      equipamentos
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};